import { useState, useEffect } from 'react';
import Layout from '../../components/feature/Layout';
import { useReveal } from '../../hooks/useReveal';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase, Service } from '../../lib/supabase';
import { services as mockServices } from '../../mocks/services';

function getLocText(i18n: Record<string, string> | null | undefined, lang: string, fallback: string): string {
  if (!i18n) return fallback;
  return i18n[lang] || i18n['ko'] || fallback;
}

function getLocArr(i18n: Record<string, string[]> | null | undefined, lang: string, fallback: string[]): string[] {
  if (!i18n) return fallback;
  return i18n[lang] || i18n['ko'] || fallback;
}

export default function ServicesPage() {
  const ref = useReveal();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [services, setServices] = useState<Service[]>(mockServices as Service[]);

  useEffect(() => {
    supabase.from('services').select('*').order('sort_order')
      .then(({ data, error }) => {
        if (error) {
          console.warn('[Services] Supabase error, using mock data:', error.message);
          setServices(mockServices as Service[]);
        } else if (data && data.length > 0) {
          setServices(data as Service[]);
        }
      });
  }, []);

  return (
    <Layout>
      <title>{t('page.title.services')}</title>
      <meta name="description" content="End-to-end Korea-Vietnam trade services: maritime logistics, air freight, customs compliance, market entry advisory, trade finance, and warehousing & distribution." />
      <meta name="keywords" content="Korea Vietnam trade services, maritime logistics Korea Vietnam, customs compliance, market entry advisory, trade finance Seoul HCMC" />

      {/* Hero */}
      <section className="relative pt-28 pb-16 lg:pt-36 lg:pb-24 bg-navy overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/5c075ffec12fd2f13a8c7453252c207f.jpg"
            alt="Services"
            className="w-full h-full object-cover object-top opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-navy/90 to-navy/60" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <p className="text-xs font-sans text-silver/50 tracking-[0.2em] uppercase mb-4">{t('servicesPage.eyebrow')}</p>
          <h1 className="font-serif text-white text-3xl sm:text-4xl lg:text-6xl font-bold leading-tight mb-4 lg:mb-5 max-w-2xl">
            {t('servicesPage.heading1')}
          </h1>
          <p className="text-white/60 font-sans text-base lg:text-lg leading-relaxed max-w-xl">
            {t('servicesPage.heroDesc')}
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section ref={ref} className="bg-white py-14 lg:py-28">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-8">
            {services.map((svc, i) => {
              const title = getLocText(svc.title_i18n as Record<string, string> | null, lang, svc.title);
              const description = getLocText(svc.description_i18n as Record<string, string> | null, lang, svc.description);
              const features = getLocArr(svc.features_i18n as Record<string, string[]> | null, lang, svc.features);
              return (
                <div
                  key={svc.id}
                  className={`reveal stagger-${(i % 4) + 1} group rounded-2xl border border-silver-light overflow-hidden flex flex-col sm:flex-row hover:-translate-y-0.5 transition-all duration-300`}
                >
                  <div className="h-44 sm:h-auto sm:w-52 flex-shrink-0 overflow-hidden">
                    <img src={svc.image_url} alt={title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-5 lg:p-7 flex flex-col">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-9 h-9 flex items-center justify-center bg-navy rounded-lg flex-shrink-0">
                        <i className={`${svc.icon} text-silver text-base`} />
                      </div>
                      <h3 className="font-serif text-navy text-lg lg:text-xl font-semibold">{title}</h3>
                    </div>
                    <p className="text-navy/60 text-sm font-sans leading-relaxed mb-4 flex-1">{description}</p>
                    <ul className="space-y-1.5 mb-5">
                      {features.map((f) => (
                        <li key={f} className="flex items-center gap-2 text-xs text-navy/50 font-sans">
                          <i className="ri-check-line text-navy/30 flex-shrink-0" />
                          {f}
                        </li>
                      ))}
                    </ul>
                    <Link to="/contact" className="text-sm font-sans font-medium text-navy flex items-center gap-1 hover:gap-2 transition-all duration-200 cursor-pointer">
                      {t('servicesPage.enquireNow')} <i className="ri-arrow-right-line" />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy py-14 lg:py-20">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-serif text-white text-2xl lg:text-4xl font-bold mb-4">{t('servicesPage.cta.heading')}</h2>
          <p className="text-white/60 font-sans text-sm lg:text-base mb-8">{t('servicesPage.cta.desc')}</p>
          <Link to="/contact" className="inline-flex items-center gap-2 bg-white text-navy text-sm font-sans font-semibold px-8 py-4 rounded hover:bg-silver-light transition-all duration-200 cursor-pointer whitespace-nowrap">
            {t('servicesPage.cta.btn')} <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </section>
    </Layout>
  );
}
